import { countryMapList } from '../data/countryMapList';
import isoMap from '../data/isoCodeMap';
import { type } from './constants';
import {
  getPurchaseValue,
  havePaid,
  signUpStatus
} from './enrolmentStatus';

export const formatEnrolmentCSVData = (data) => {
  const headers = [
    { label: 'First Name', key: 'firstName' },
    { label: 'Last Name', key: 'lastName' },
    { label: 'Email', key: 'email' },
    { label: 'Country', key: 'country' },
    { label: 'ISO Code', key: 'isoCode' },
    { label: 'Phone', key: 'phone' },
    { label: 'Purchase Value (USD)', key: 'purchaseValue' },
    { label: 'Course Offer Code', key: 'courseOfferCode' },
    { label: 'Sign Up Time', key: 'createdTime' },
    { label: 'Enrolment', key: 'enrolmentStatus' },
    { label: 'Status', key: 'status' }
  ];

  const rows = data.map((d) => {
    return {
      firstName: d.student_details?.basic_info?.student_info?.first_name,
      lastName: d.student_details?.basic_info?.student_info?.last_name,
      email: d.student_details?.basic_info?.student_info?.email,
      country:
        countryMapList[
          d.student_details?.basic_info?.student_info?.country_id
        ]?.name,
      isoCode: countryMapList[
        d.student_details?.basic_info?.student_info?.country_id
      ]?.isoCode?.toUpperCase(),
      phone: d.student_details?.basic_info?.student_info?.phone_number,
      purchaseValue: getPurchaseValue(d.payment_details),
      courseOfferCode: d.student_details?.course_code,
      createdTime: new Date(
        d.student_details?.create_time
      ).toLocaleString(),
      enrolmentStatus: havePaid(d) ? havePaid(d) : 'NIL',
      status: signUpStatus(d.payment_details)
    };
  });

  return {
    data: rows,
    headers,
    filename: `Sign_Up_Export.csv`
  };
};

export const formatAlumniCSVData = (data) => {
  const headers = [
    { label: 'Name', key: 'fullName' },
    { label: 'Email', key: 'email' },
    { label: 'Country', key: 'country' },
    { label: 'Phone', key: 'phone' },
    { label: 'Course Code', key: 'courseCode' },
    { label: 'Course Start Date', key: 'startDate' },
    { label: 'Course Title', key: 'courseTitle' },
    { label: 'Repeat', key: 'repeat' }
  ];

  const rows = data.map((d) => {
    return {
      fullName: d?.fullName,
      email: d?.email,
      country: countryMapList[d?.countryId]?.name,
      phone: d?.phoneNumber,
      courseCode: d?.courseCode,
      startDate: d?.startDate,
      courseTitle: d?.courseTitle,
      repeat: d?.repeat
    };
  });

  return {
    data: rows,
    headers,
    filename: `Alumni_Export.csv`
  };
};

export const formatRefundCSVData = (data) => {
  const headers = [
    { label: 'Refund Date', key: 'dateRefunded' },
    { label: 'Purchase Date', key: 'datePurchased' },
    { label: 'Refund Period', key: 'refundPeriod' },
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Course Code', key: 'courseCode' },
    { label: 'Is Partial', key: 'isPartial' },
    { label: 'Amount', key: 'amount' },
    { label: 'Reason', key: 'reason' }
  ];

  const rows = data.map((d) => {
    return {
      dateRefunded: d.dateRefunded
        ? new Date(d.dateRefunded).toLocaleDateString()
        : null,
      datePurchased: d.datePurchased
        ? new Date(d.datePurchased).toLocaleDateString()
        : null,
      refundPeriod: d?.refundPeriod,
      name: d?.name,
      email: d?.email,
      courseCode: d?.courseCode,
      isPartial: d?.isPartial,
      amount: d?.amount,
      reason: d?.reason
    };
  });

  return {
    data: rows,
    headers,
    filename: `Refund_Export.csv`
  };
};

export const formatTrialClassSignupCSVData = (data) => {
  const headers = [
    { label: 'First Name', key: 'firstName' },
    { label: 'Last Name', key: 'lastName' },
    { label: 'Email', key: 'email' },
    { label: 'Country', key: 'country' },
    { label: 'Phone Number', key: 'phoneNumber' },
    { label: 'ISO Code', key: 'isoCode' },
    { label: 'Class Code', key: 'classCode' },
    { label: 'Class Timezone', key: 'timezone' },
    { label: 'Class Time', key: 'classTime' },
    { label: 'Sign up time', key: 'signupTime' }
  ];

  const rows = data.map((d) => {
    return {
      firstName: d.firstName,
      lastName: d.lastName,
      email: d.email,
      country: d.country,
      isoCode: isoMap[d.country]?.Code,
      phoneNumber: d.dialCode + d.phone,
      classCode: d.class?.classCode,
      timezone: d.class?.timezone,
      classTime: d.class
        ? new Date(d.class?.startDate).toLocaleDateString()
        : null,
      signupTime: new Date(d.createdAt).toLocaleDateString()
    };
  });

  return {
    data: rows,
    headers,
    filename: `Trial_Class_Sign_Up_Export.csv`
  };
};

export const capitalizeFirstChar = (str) => {
  return str ? str[0].toUpperCase() + str.slice(1) : '';
};

export const getPromoStatusLabelByType = (type, status) => {
  const typeToStatusMap = {
    giftCard: {
      created: 'Created',
      pending: 'Pending Payment',
      completed: 'Payment Completed',
      used: 'Used'
    },
    referral: {
      pending: 'Pending',
      used: 'Used'
    },
    coupon: {
      used: 'Used'
    }
  };

  const label =
    typeToStatusMap[type]?.[status] || capitalizeFirstChar(status);
  return label;
};

export const formatPromoTransactionsCsvData = (data) => {
  const headers = [
    {
      label: 'Promo Code',
      key: 'promoCode'
    },
    {
      label: 'Transactor',
      key: 'transactorEmail'
    },
    {
      label: 'Type',
      key: 'type'
    },
    {
      label: 'Discount',
      key: 'discount'
    },
    {
      label: 'Discount Amount',
      key: 'amount'
    },
    {
      label: 'Credit',
      key: 'credit'
    },
    {
      label: 'Status',
      key: 'status'
    },
    {
      label: 'Created at',
      key: 'createdAt'
    }
  ];

  const rows = data.map((d) => {
    const createdAt = d.createdAt || '';
    const createdAtStr = createdAt
      ? new Date(createdAt).toLocaleDateString()
      : '';
    return {
      promoCode: d.promoId?.code,
      transactorEmail: d.transactorId?.email,
      type: d.type,
      discount:
        (d.promoId?.discountType === 'amount_off'
          ? d.promoId?.discountValue / 100
          : d.promoId?.discountValue) + `(${d.promoId?.discountType})`,
      amount: d.amount / 100,
      credit: d.credit / 100,
      status: d.status,
      createdAt: createdAtStr
    };
  });

  return {
    data: rows,
    headers,
    filename: `Promo_Transactions_Export.csv`
  };
};

export const formattedPromoCodeCsvData = (data) => {
  const headers = [
    {
      label: 'Code',
      key: 'code'
    },
    {
      label: 'Is Active?',
      key: 'isActive'
    },
    {
      label: 'Type',
      key: 'type'
    },
    {
      label: 'Value',
      key: 'discountValue'
    },
    {
      label: 'Discount type',
      key: 'discountType'
    },
    {
      label: 'Courses allowed',
      key: 'coursesAllowed'
    },
    {
      label: 'Course offers allowed',
      key: 'courseOffersAllowed'
    },
    {
      label: 'Referrer/Gifted by',
      key: 'giftedBy'
    },
    {
      label: 'Assigned to (gift)',
      key: 'assignedTo'
    },
    {
      label: 'No of usage',
      key: 'numberOfUsages'
    },
    {
      label: 'Max allowed usages',
      key: 'maxAllowedUsages'
    },
    {
      label: 'Max usage per person',
      key: 'maxUsagesPerPerson'
    },
    {
      label: 'Start date',
      key: 'startDate'
    },
    {
      label: 'End date',
      key: 'endDate'
    }
  ];

  const rows = data.map((d) => {
    const startDateStr = d.startDate
      ? new Date(d.startDate).toLocaleDateString()
      : '';
    const endDateStr = d.endDate
      ? new Date(d.endDate).toLocaleDateString()
      : '';

    let giftedBy = '';

    if (d.type === 'referraral') {
      const referrerEmail = d.metadata?.referrerId?.email;
      const referrerType = d.metadata?.referrerId?.learnerId
        ? 'Learner'
        : 'Trainer';
      giftedBy = `${referrerEmail} (${referrerType})`;
    } else if (d.type === 'giftCard') {
      giftedBy = d.metadata?.giftedBy?.email || '';
    } else {
      giftedBy = '';
    }

    return {
      code: d.code,
      isActive: d.isActive ? 'True' : 'False',
      type: d.type,
      discountValue:
        d.discountType === 'amount_off'
          ? d.discountValue / 100
          : d.discountValue,
      discountType: d.discountType,
      coursesAllowed: d.coursesAllowed.join(', '),
      courseOffersAllowed: d.courseOffersAllowed.join(', '),
      giftedBy,
      assignedTo: d.assignedTo.join(', '),
      numberOfUsages: d.numberOfUsages,
      maxAllowedUsages: d.maxAllowedUsages,
      maxUsagesPerPerson: d.maxUsagesPerPerson,
      startDate: startDateStr,
      endDate: endDateStr
    };
  });

  return {
    data: rows,
    headers,
    filename: `Promo_Codes_Export.csv`
  };
};

// Exclude nasdaily.com domain email
export const validEmail = (email) => {
  return !(
    email.includes('nasdaily.com') || email.includes('thenasacademy.com')
  );
};

export const formatFeedbackCSVData = (
  surveyData,
  averageRating,
  courseOffer,
  unitOffer
) => {
  const headers = [{ label: 'Question', key: 'question' }];
  const rows = [];
  const data = Object.values(surveyData);

  data[0].data.forEach((item, index) => {
    headers.push({ label: item.name, key: `data_${index}` });
  });
  headers.push({ label: 'Average rating', key: 'averageRating' });

  data.map((d) => {
    const row = {};
    row.question = d.label;
    d.data.forEach((item, i) => {
      row[`data_${i}`] = item.answer;
    });
    rows.push(row);
  });

  const ratingRow = { question: 'CreatorRating' };
  data[0].data.forEach((item, i) => {
    ratingRow[`data_${i}`] = item.rating;
  });
  ratingRow.averageRating = averageRating;
  rows.push(ratingRow);

  return {
    data: rows,
    headers,
    filename: `Feedback_Course_${courseOffer}_Lesson_${unitOffer}.csv`
  };
};

export const addSorters = (tableData) => {
  return tableData.map((data) => {
    const skipSorter = data?.skipInjectedSorter ?? false;

    //skip adding the below sorter functionality
    if (skipSorter) return data;

    const dataIndex = data?.dataIndex;
    const dataType = data?.type;
    const sortDirections = ['descend', 'ascend'];
    const sorter =
      dataType === type.NUMBER
        ? function (a, d) {
            const numA = a?.[dataIndex] ?? 0;
            const numD = d?.[dataIndex] ?? 0;
            return numA - numD;
          }
        : function (a, d) {
            const strA = a?.[dataIndex]?.toString() ?? '';
            const strD = d?.[dataIndex]?.toString() ?? '';
            return strA.localeCompare(strD);
          };

    return {
      sorter,
      sortDirections,
      ...data
    };
  });
};

export const formatNumberToIntl = (num) =>
  new Intl.NumberFormat('en-US').format(num);
